import {
    RepeatWrapping,
    CanvasTexture,
    MeshPhysicalMaterial,
    TextureLoader,
    BoxGeometry,
    Mesh,
    LinearMipMapNearestFilter,
    Box3,
    Vector3,
    MirroredRepeatWrapping,
    MeshStandardMaterial,
    Color,
    BufferAttribute,
    // BufferGeometry,
} from "three";

import {
    top,
    right,
    bottom,
    left,
    topLeft,
    topRigth,
    bottomLeft,
    bottomRight,
    wall,
    cabinet,
    molding,
    marble,
    realHardwoods,
    glassT,
    fabric,
    fabric3,
    // metal,
} from "textures";
// import uvgrid from "textures/uv_grid_opengl.jpg";
import { getMaterial } from "materials";

// ==== NormalMap ====

const createNormalMap = (width, height, gap, tileThickness) => {
    const imgTop = new Image();
    const imgLeft = new Image();
    const imgBottom = new Image();
    const imgRight = new Image();
    const imgTopRight = new Image();
    const imgTopLeft = new Image();
    const imgBottomLeft = new Image();
    const imgBottomRight = new Image();
    imgTop.src = top;
    imgBottom.src = bottom;
    imgRight.src = right;
    imgLeft.src = left;
    imgTopRight.src = topRigth;
    imgTopLeft.src = topLeft;
    imgBottomLeft.src = bottomLeft;
    imgBottomRight.src = bottomRight;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "rgb(128, 127, 255)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
        imgTop,
        gap / 2 + tileThickness,
        gap / 2,
        canvas.width - gap - 2 * tileThickness,
        tileThickness
    );

    ctx.drawImage(
        imgRight,
        canvas.width - gap / 2 - tileThickness,
        gap / 2 + tileThickness,
        tileThickness,
        canvas.height - gap - 2 * tileThickness
    );

    ctx.drawImage(
        imgBottom,
        gap / 2 + tileThickness,
        canvas.height - gap / 2 - tileThickness,
        canvas.width - gap - 2 * tileThickness,
        tileThickness
    );

    ctx.drawImage(
        imgLeft,
        gap / 2,
        gap / 2 + tileThickness,
        tileThickness,
        canvas.height - gap - 2 * tileThickness
    );

    ctx.drawImage(imgTopLeft, gap / 2, gap / 2, tileThickness, tileThickness);
    ctx.drawImage(
        imgTopRight,
        canvas.width - gap / 2 - tileThickness,
        gap / 2,
        tileThickness,
        tileThickness
    );
    ctx.drawImage(
        imgBottomRight,
        canvas.width - gap / 2 - tileThickness,
        canvas.height - gap / 2 - tileThickness,
        tileThickness,
        tileThickness
    );
    ctx.drawImage(
        imgBottomLeft,
        gap / 2,
        canvas.height - gap / 2 - tileThickness,
        tileThickness,
        tileThickness
    );

    const normalMap = new CanvasTexture(canvas);

    return normalMap;
};

// === get Params ===

export const getMeshParams = (mesh, scale = 1) => {
    const box = new Box3().setFromObject(mesh);
    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);

    const normal = new Vector3();
    mesh.geometry.computeVertexNormals();
    normal.fromBufferAttribute(mesh.geometry.getAttribute("normal"), 0);
    // console.log("normal: ", normal);

    const rotate = [
        (Math.PI / 2) * Math.round(-normal.y),
        (Math.PI / 2) * Math.round(normal.x),
        (Math.PI / 2) * Math.round(normal.z),
    ];

    const position = [center.x * scale, center.z * scale, center.y * scale];
    // console.log("position: ", position);
    const width = Math.max(size.x, size.y) * scale;
    const length = Math.min(size.x, size.y) * scale;
    const height = size.z * scale;
    // console.log("width: ", width);
    // console.log("height: ", height);

    return { rotate, position, width, height, length };
};

// === SeamMaterial ===

export const getSeamMaterial = (seamColor) => {
    const texture = new TextureLoader().load(wall);
    texture.wrapS = MirroredRepeatWrapping;
    texture.wrapT = MirroredRepeatWrapping;

    return new MeshPhysicalMaterial({
        map: texture,
        color: seamColor,
        clearcoat: 0,
        roughness: 1,
    });
};

// === Wallpaper ===

export const getWallpaper = (texture, width) => {
    const image = new Image();
    image.src = texture;
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 1024;

    const ctx = canvas.getContext("2d");

    ctx.translate(0, canvas.height / 2);
    ctx.scale(1, -1);
    ctx.translate(0, -canvas.height / 2);

    ctx.drawImage(
        image,
        0,
        0,
        image.width * (width / 100),
        image.height,
        0,
        0,
        canvas.width,
        canvas.height
    );

    const newTexture = new CanvasTexture(canvas);
    newTexture.repeat.set(270 / width, 3);
    newTexture.wrapS = RepeatWrapping;
    newTexture.wrapT = RepeatWrapping;

    // newTexture.magFilter = LinearMipMapNearestFilter;
    // newTexture.minFilter = LinearMipMapNearestFilter;

    const material = new MeshStandardMaterial({
        map: newTexture,
        color: "rgb(175, 175, 175)",
        roughness: 1,
    });

    return material;
};

// === CarpetMaterial ===

export const getCarpetMaterial = (texture, inch, length, turn, color) => {
    let newTexture;
    if (length === 2) {
        inch = 6;
        newTexture = new TextureLoader().load(texture);
    } else {
        const realTexture = inch === 6 ? realHardwoods[0] : realHardwoods[1];
        newTexture = new TextureLoader().load(realTexture);
    }
    newTexture.rotation = Math.PI * 0.5 * turn;
    newTexture.repeat.set(13.2 / 6, 1 * length);
    newTexture.wrapS = RepeatWrapping;
    newTexture.wrapT = RepeatWrapping;

    const material = new MeshPhysicalMaterial({
        map: newTexture,
        roughness: 1,
    });
    if (length === 1) material.color = new Color(color);

    return material;
};

// === Tile Edge ===

export const createEdge = (mesh) => {
    const box = new Box3().setFromObject(mesh);
    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);
    // console.log("center: ", center);

    const normal = new Vector3();
    mesh.geometry.computeVertexNormals();
    normal.fromBufferAttribute(mesh.geometry.getAttribute("normal"), 0);

    // console.log("normal: ", normal);
    const wallWidth = Math.max(size.x, size.y);
    const wallHeight = size.z;

    const coor = size.x > size.y ? center.x : center.y;
    const edgeWidht = 5;

    const geometry = new BoxGeometry(edgeWidht, wallHeight, 1);

    const rotate = [
        (Math.PI / 2) * Math.round(normal.x - normal.y),
        (Math.PI / 2) * Math.round(normal.x),
        (Math.PI / 2) * Math.round(normal.z),
    ];

    const position = [center.x, center.y, center.z];

    const material = getMaterial("metal", "rgb(229,229,229)");

    const edge = new Mesh(geometry, material);
    edge.turning = rotate;
    edge.location = position;
    edge.horizontalIndex = size.x > size.y ? 0 : 1;
    edge.box = {
        left: coor - wallWidth / 2,
        right: coor + wallWidth / 2,
        top: center.z + wallHeight / 2,
        bottom: center.z - wallWidth / 2,
    };
    edge.boxCenter = center;
    edge.size = [1, 1, 1];
    edge.wall = mesh;
    edge.wallSize = { width: wallWidth, height: wallHeight };
    edge.isHorizontal = false;

    return edge;
};

// === create Material ===

export const chooseMaterial = (mesh, types) => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (types.metal.includes(mesh.name)) {
        mesh.receiveShadow = false;
        const material = getMaterial("metal", "rgb(229,229,229)");
        return material;
    }
    if (mesh.name === "Drain_Grate") {
        return getMaterial("metal", "rgb(40, 40, 43)");
    }
    if (types.wall.includes(mesh.name)) {
        return getMaterial("wall", "rgb(100,100,100)");
    }

    if (types.textile.includes(mesh.name)) {
        const material = getMaterial("textile", "rgb(255, 255, 255)");
        const texture = new TextureLoader().load(fabric);
        // texture.repeat.set(3, 3);
        texture.wrapS = MirroredRepeatWrapping;
        texture.wrapT = MirroredRepeatWrapping;
        material.map = texture;
        return material;
    }

    // if (types.textile5.includes(mesh.name)) {
    //     const material = getMaterial("textile", "rgb(255, 255, 255)");
    //     const texture = new TextureLoader().load(fabric);
    //     // texture.repeat.set(3, 3);
    //     texture.wrapS = MirroredRepeatWrapping;
    //     texture.wrapT = MirroredRepeatWrapping;
    //     material.map = texture;
    //     return material;
    // }
    if (types.textile2.includes(mesh.name)) {
        const material = getMaterial("textile", "rgb(50,40,10)");
        const texture = new TextureLoader().load(fabric);
        // texture.repeat.set(3, 3);
        texture.wrapS = MirroredRepeatWrapping;
        texture.wrapT = MirroredRepeatWrapping;
        material.map = texture;
        return material;
    }
    if (types.textile3.includes(mesh.name)) {
        const material = getMaterial("textile", "rgb(50,40,10)");
        const texture = new TextureLoader().load(fabric);
        texture.wrapS = MirroredRepeatWrapping;
        texture.wrapT = MirroredRepeatWrapping;
        material.map = texture;
        return material;
    }
    if (types.textile4.includes(mesh.name)) {
        const material = getMaterial("textile", "rgb(127,127,127)");
        const texture = new TextureLoader().load(fabric3);
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        material.map = texture;
        return material;
    }

    if (types.emissive.includes(mesh.name)) {
        return getMaterial("emissive", "rgb(255,255,255)");
    }

    if (types.plastic.includes(mesh.name)) return getMaterial("plastic", "rgb(255,255,255)");

    if (types.plastic2.includes(mesh.name)) return getMaterial("plastic", "rgb(71, 49, 41)");

    if (types.stone.includes(mesh.name)) {
        const material = getMaterial("marble", "rgb(128,128,128)");
        const texture = new TextureLoader().load(marble);

        material.map = texture;

        return material;
    }
    if (types.wood.includes(mesh.name)) {
        const material = getMaterial("wood", "rgb(71, 49, 41)");
        const texture = new TextureLoader().load(molding);
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        material.map = texture;
        return material;
    }
    if (types.wood2.includes(mesh.name)) {
        const material = getMaterial("wood", "rgb(169, 151, 130)");
        const texture = new TextureLoader().load(cabinet);
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        material.map = texture;
        return material;
    }
    if (mesh.name === "Ceiling" || mesh.name === "Ceiling_1") {
        const material = getMaterial("wall", "rgb(255, 255, 255)");
        const texture = new TextureLoader().load(wall);
        texture.wrapS = MirroredRepeatWrapping;
        texture.wrapT = MirroredRepeatWrapping;
        material.map = texture;
        return material;
    }
    if (types.glass.includes(mesh.name)) {
        mesh.castShadow = false;
        return getMaterial("glass", "rgb(255, 255, 255)", 0.5);
    }
    if (types.window.includes(mesh.name)) {
        mesh.castShadow = false;
        return getMaterial("glass", "rgb(255, 255, 255)", 0.2);
    }
    if (types.ceramic.includes(mesh.name)) {
        // mesh.receiveShadow = false;
        return getMaterial("ceramic", "rgb(255, 255, 255)");
    }
    if (mesh.name === "Mesh032") {
        const material = getMaterial("frostedGlass", "rgb(150, 150, 150)");
        const texture = new TextureLoader().load(glassT);
        material.map = texture;
        mesh.receiveShadow = false;
        return material;
    }

    if (types.notReceiveShadow.includes(mesh.name)) {
        mesh.receiveShadow = false;
    }

    if (mesh.name === "Drain") {
        return getMaterial("plastic", "rgb(30,30,30)");
    }

    return mesh.material;
};

// === get Meshes ===

export const getMeshes = (root, mirrors = [], types) => {
    const meshes = [];
    if (root.children.length > 0)
        root.children.forEach((child) => {
            // const { meshes: children } = getMeshes(child, mirrors);
            meshes.push(...getMeshes(child, mirrors, types).meshes);
        });
    else {
        if (types.mirror.includes(root.name)) {
            mirrors.push(root);
        } else {
            root.material = chooseMaterial(root, types);
            // root.needsUpdate = true;
            meshes.push(root);
        }
    }

    return { meshes, mirrors };
};

// === change material ====
export const changeMaterial = (prevMaterial, newMaterial) => {
    const { color, texture, imgW, imgH, isRemove } = newMaterial;
    if (color) {
        prevMaterial.color = new Color(color);
        // prevMaterial.sheenColor = new Color(color);
        if (prevMaterial.emissive) prevMaterial.emissive = new Color(color);
        // prevMaterial.emissive = new Color(color);
        // prevMaterial.emissiveIntensity = 0.15;
    }
    if (texture) {
        const scaleX = 182.8684 / imgW;
        const scaleY = 56.7572 / imgH;

        const offsetX = 0.952 - 0.052 * (scaleX - 1);
        const offsetY = 0.772 - 0.222 * (scaleY - 1);

        const newTexture = new TextureLoader().load(texture);
        newTexture.repeat.set(1.48 * scaleX, 4.96 * scaleY);

        newTexture.offset = { x: offsetX, y: offsetY };
        // newTexture.wrapS = RepeatWrapping;
        // newTexture.wrapT = RepeatWrapping;
        newTexture.wrapS = MirroredRepeatWrapping;
        newTexture.wrapT = MirroredRepeatWrapping;
        prevMaterial.map = newTexture;
    }
    if (isRemove) {
        prevMaterial.map = null;
    }

    prevMaterial.needsUpdate = true;
    // console.log("prevMaterial: ", prevMaterial);
    // return prevMaterial;
};

// === MAIN FUNCTION ====

export const getMaterials = (mesh, settings) => {
    const {
        texture,
        tileWidth,
        tileHeight,
        pos,
        seamColor,
        seamSize,
        material,
        turned,
        normalMap,
    } = settings;

    const width = pos === "horizontal" ? tileWidth : tileHeight;
    const height = pos === "horizontal" ? tileHeight : tileWidth;

    const image = new Image();
    image.src = texture;
    const scale = image.width / Math.max(width, height);
    const gap = seamSize * scale;
    const vw = Math.min(1, width / height);
    const vh = Math.min(1, height / width);
    // let baseSize = mesh.name === "Drain" ? 193 : 270;
    // if (settings.tiling !== "whole") baseSize *= 0.5;

    const meshParam = getMeshParams(mesh);
    console.log("meshParam: ", meshParam);
    let baseSizeW, baseSizeH;
    if (settings.tiling === "whole") {
        baseSizeW = baseSizeH = meshParam.height;
    } else {
        // baseSizeW = meshParam.width;
        baseSizeW = meshParam.height;
        // baseSizeH = meshParam.height * 0.5;
        baseSizeH = meshParam.height;
    }

    const canvas = document.createElement("canvas");
    canvas.width = image.width * vw + gap;
    canvas.height = image.height * vh + gap;

    const ctx = canvas.getContext("2d");

    ctx.translate(0, canvas.height / 2);
    ctx.scale(1, -1);
    ctx.translate(0, -canvas.height / 2);

    ctx.fillStyle = seamColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let imgWidth = image.width;
    let imgHeight = image.height * vh;
    let visibleWidth = canvas.width - gap;
    let visibleHeight = canvas.height - gap;

    if (pos === "vertical") {
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.translate(-canvas.height / 2, -canvas.width / 2);

        imgHeight = image.height * vw;
        visibleWidth = canvas.height - gap;
        visibleHeight = canvas.width - gap;
    }

    if (turned) {
        if (pos === "vertical") {
            ctx.translate(canvas.height / 2, canvas.width / 2);
            ctx.rotate(Math.PI);
            ctx.translate(-canvas.height / 2, -canvas.width / 2);
        } else {
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(Math.PI);
            ctx.translate(-canvas.width / 2, -canvas.height / 2);
        }
    }

    ctx.drawImage(image, 0, 0, imgWidth, imgHeight, gap / 2, gap / 2, visibleWidth, visibleHeight);

    const newTexture = new CanvasTexture(canvas);

    newTexture.repeat.set(baseSizeW / (width + seamSize), baseSizeH / (height + seamSize));

    newTexture.magFilter = LinearMipMapNearestFilter;
    newTexture.minFilter = LinearMipMapNearestFilter;
    newTexture.wrapS = RepeatWrapping;
    newTexture.wrapT = RepeatWrapping;

    let normalTileMap;

    if (!normalMap) {
        const tileThickness = 27 * Math.min(vw, vh);
        normalTileMap = createNormalMap(canvas.width, canvas.height, gap, tileThickness);
    } else {
        normalTileMap = new TextureLoader().load(normalMap);
    }
    normalTileMap.repeat.set(baseSizeW / (width + seamSize), baseSizeH / (height + seamSize));

    normalTileMap.magFilter = LinearMipMapNearestFilter;
    normalTileMap.minFilter = LinearMipMapNearestFilter;

    normalTileMap.wrapS = RepeatWrapping;
    normalTileMap.wrapT = RepeatWrapping;

    const canvas2 = document.createElement("canvas");
    canvas2.width = canvas.width;
    canvas2.height = canvas.height;

    const ctx2 = canvas2.getContext("2d");

    ctx2.fillStyle = seamColor;
    ctx2.fillRect(0, 0, canvas2.width, gap / 2);
    ctx2.fillRect(0, 0, gap / 2, canvas2.height);
    ctx2.fillRect(canvas2.width - gap / 2, 0, gap / 2, canvas2.height);
    ctx2.fillRect(0, canvas2.height - gap / 2, canvas2.width, gap / 2);

    const groutTexture = new CanvasTexture(canvas2);

    groutTexture.repeat.set(baseSizeW / (width + seamSize), baseSizeH / (height + seamSize));

    groutTexture.magFilter = LinearMipMapNearestFilter;
    groutTexture.minFilter = LinearMipMapNearestFilter;
    groutTexture.wrapS = RepeatWrapping;
    groutTexture.wrapT = RepeatWrapping;

    const material1 = new MeshPhysicalMaterial({
        map: groutTexture,
        clearcoat: 0,
        roughness: 1,
        transparent: true,
    });

    const material2 = new MeshPhysicalMaterial({
        map: newTexture,
        envMap: newTexture,
        normalMap: normalTileMap,
        emissiveIntensity: 1,
        clearcoat: material === "polish" ? 0.5 : 0,
        roughness: material === "polish" ? 0.3 : 1,
        metalness: 0.2,
    });

    return [material1, material2];
};

const create = (mesh, center, pos) => {
    const vertices = mesh.geometry.attributes.position.array;
    const uvs = mesh.geometry.attributes.uv.array;
    const clippedVertices = new Float32Array([...vertices]);
    const clippedUvs = new Float32Array([...uvs]);

    for (let i = 2, j = 1; i < vertices.length; i += 3, j += 2) {
        clippedVertices[i] = vertices[i];
        if (pos === "top") {
            if (clippedVertices[i] > center.z) {
                clippedVertices[i] = center.z;
                clippedUvs[j] = 0.5;
            }
        }
        if (pos === "bottom") {
            if (clippedVertices[i] < center.z) {
                clippedVertices[i] = center.z;
                clippedUvs[j] = 0.5;
            }
        }
    }

    const geometry = mesh.geometry.clone();
    geometry.setAttribute("position", new BufferAttribute(clippedVertices, 3));
    geometry.setAttribute("uv", new BufferAttribute(clippedUvs, 2));
    return geometry;
};

export const createMaterials = (mesh, options) => {
    const [seamMaterial, tileMaterial] = getMaterials(mesh, options);
    const { tiling } = options;
    const box = new Box3().setFromObject(mesh);

    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);

    const normal = new Vector3();
    mesh.geometry.computeVertexNormals();
    normal.fromBufferAttribute(mesh.geometry.getAttribute("normal"), 0);

    let { groutTop, groutBottom, tileTop, tileBottom } = mesh.userData;

    if (tiling === "top") {
        const geometry = create(mesh, center, tiling);
        groutTop = new Mesh(geometry, seamMaterial);
        groutTop.position.set(
            groutTop.position.x + 0.01 * normal.x,
            groutTop.position.y + 0.01 * normal.y,
            groutTop.position.z + 0.01 * normal.z
        );

        tileTop = new Mesh(geometry, tileMaterial);
        tileTop.position.set(
            groutTop.position.x + 0.01 * normal.x,
            groutTop.position.y + 0.01 * normal.y,
            groutTop.position.z + 0.01 * normal.z
        );
    } else {
        const geometry = create(mesh, center, tiling);
        groutBottom = new Mesh(geometry, seamMaterial);
        groutBottom.position.set(
            groutBottom.position.x + 0.01 * normal.x,
            groutBottom.position.y + 0.01 * normal.y,
            groutBottom.position.z + 0.01 * normal.z
        );

        tileBottom = new Mesh(geometry, tileMaterial);
        tileBottom.position.set(
            groutBottom.position.x + 0.01 * normal.x,
            groutBottom.position.y + 0.01 * normal.y,
            groutBottom.position.z + 0.01 * normal.z
        );
    }

    return [groutTop, groutBottom, tileTop, tileBottom];
};
